<template>
  <div id="compromisso-servico-modal-app">
    <!-- Edit Modal -->
    <modal
      elem-id="modalCompromissoServico"
      size="compactDesktop"
      :close-label="t('Fechar')"
      :save-label="t('Salvar serviço')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="t('Serviço do compromisso')"
      :show="isEditModalVisible"
      @close="closeEditModal"
      @dismiss="closeEditModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isEditModalVisible">
        <!-- Action Buttons -->
      <div class="action-buttons">
        <Button
          data-testid="compromissoservico-reativar-button"
          v-if="shouldShowReativar"
          @click="reativarCompromissoServico"
          severity="success"
          :label="t('Reativar serviço')"
          icon="pi pi-check"
        />
        <Button
          data-testid="compromissoservico-cancelar-button"
          v-if="shouldShowCancelar"
          @click="cancelarCompromissoServico"
          severity="danger"
          :label="t('Cancelar serviço')"
          icon="pi pi-ban"
        />
        <Button
          data-testid="compromissoservico-excluir-button"
          v-if="shouldShowExcluir"
          @click="excluirCompromissoServico"
          severity="danger"
          :label="t('Excluir serviço')"
          icon="pi pi-trash"
        />
      </div>

        <compromisso-servico-form
          ref="formRef"
          :is-edit-mode="true"
          :compromisso-id="currentCompromissoId"
          :orcamento-id="currentOrcamentoId"
          :franquia-id="currentFranquiaId"
          @save-success="handleSaveSuccess"
          @action-success="handleActionSuccess">
        </compromisso-servico-form>
      </template>
    </modal>

    <!-- New Modal -->
    <modal
      elem-id="modalNovoCompromissoServico"
      :close-label="t('Fechar')"
      :save-label="t('Salvar serviço')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSaveNovo"
      :title="t('Serviço do compromisso')"
      :show="isNewModalVisible"
      @close="closeNewModal"
      @dismiss="closeNewModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isNewModalVisible">
        <compromisso-servico-form
          ref="formRef"
          :is-edit-mode="false"
          :compromisso-id="currentCompromissoId"
          :orcamento-id="currentOrcamentoId"
          :franquia-id="currentFranquiaId"
          @save-success="handleSaveSuccess"
          @action-success="handleActionSuccess">
        </compromisso-servico-form>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from '../../ui/Modal.vue';
import CompromissoServicoForm from './CompromissoServicoForm.vue';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { useCompromissoServico } from '../../composables/useCompromissoServico';

type Id = number | string;

interface CompromissoServicoData {
  compromisso_servico_id?: Id;
  compromisso_servico_status?: number | string;
  [key: string]: unknown;
}

interface FormInstance {
  canSave?: boolean;
  loadForEdit: (id: Id) => Promise<void>;
  initializeForNew: () => void | Promise<void>;
  saveCompromissoServico: () => Promise<void>;
  saveNovoCompromissoServico: () => Promise<void>;
  resetForm: () => void;
  getCompromissoServicoData: () => CompromissoServicoData | undefined;
}

interface ModalOpenPayload {
  id?: Id;
  compromissoId?: number;
  orcamentoId?: number;
  franquiaId?: number;
}

interface LegacyOpenPayload {
  compromissoServicoId: Id;
  compromissoId?: number;
  orcamentoId?: number;
}

const { t } = useI18n();

const { confirmAction } = useConfirmAction();
const { cancelCompromissoServico, saveCompromissoServico, removeCompromissoServico } = useCompromissoServico();

const isEditModalVisible = ref<boolean>(false);
const isNewModalVisible = ref<boolean>(false);
const formRef = ref<FormInstance | null>(null);
const currentCompromissoId = ref<number>(0);
const currentOrcamentoId = ref<number>(0);
const currentFranquiaId = ref<number>(0);
const currentCompromissoServicoId = ref<Id>(0);

const shouldShowReativar = computed<boolean>(() => {
  const status = formRef.value?.getCompromissoServicoData()?.compromisso_servico_status;
  return status === 6;
});

const shouldShowCancelar = computed<boolean>(() => {
  const status = formRef.value?.getCompromissoServicoData()?.compromisso_servico_status;
  return Boolean(status) && status !== 6 && status !== 4;
});

const shouldShowExcluir = computed<boolean>(() => {
  const status = formRef.value?.getCompromissoServicoData()?.compromisso_servico_status;
  return Boolean(status) && status !== 4;
});

const abreModal = async (compromissoServicoId: Id, compromissoId: number = 0, orcamentoId: number = 0, franquiaId: number = 0): Promise<void> => {
  log('CompromissoServicoModal', 'abreModal', 'Open edit', {compromissoServicoId, compromissoId, orcamentoId, franquiaId});
  isEditModalVisible.value = true;
  isNewModalVisible.value = false;
  currentCompromissoId.value = compromissoId;
  currentOrcamentoId.value = orcamentoId;
  currentFranquiaId.value = franquiaId;
  currentCompromissoServicoId.value = compromissoServicoId;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit(compromissoServicoId);
  }
};

const abreModalNovo = async (compromissoId: number, orcamentoId: number = 0, franquiaId: number = 0): Promise<void> => {
  log('CompromissoServicoModal', 'abreModalNovo', 'Open new', {compromissoId, orcamentoId, franquiaId});
  isNewModalVisible.value = true;
  isEditModalVisible.value = false;
  currentCompromissoId.value = compromissoId;
  currentOrcamentoId.value = orcamentoId;
  currentFranquiaId.value = franquiaId;
  currentCompromissoServicoId.value = 0;

  await nextTick();

  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const closeEditModal = (): void => {
  log('CompromissoServicoModal', 'closeEditModal', 'Close edit modal');
  isEditModalVisible.value = false;
  currentCompromissoId.value = 0;
  currentOrcamentoId.value = 0;
  currentFranquiaId.value = 0;
  currentCompromissoServicoId.value = 0;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const closeNewModal = (): void => {
  log('CompromissoServicoModal', 'closeNewModal', 'Close new modal');
  isNewModalVisible.value = false;
  currentCompromissoId.value = 0;
  currentOrcamentoId.value = 0;
  currentFranquiaId.value = 0;
  currentCompromissoServicoId.value = 0;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('CompromissoServicoModal', 'handleSave', 'Delegating save');
  if (formRef.value) {
    await formRef.value.saveCompromissoServico();
  }
};

const handleSaveNovo = async (): Promise<void> => {
  log('CompromissoServicoModal', 'handleSaveNovo', 'Delegating save (new)');
  if (formRef.value) {
    await formRef.value.saveNovoCompromissoServico();
  }
};

const handleSaveSuccess = (): void => {
  log('CompromissoServicoModal', 'handleSaveSuccess', 'Save success');
  closeEditModal();
  closeNewModal();
};

const handleActionSuccess = (): void => {
  log('CompromissoServicoModal', 'handleActionSuccess', 'Action success');
  closeEditModal();
  closeNewModal();
};

const cancelarCompromissoServico = (): void => {
  log('CompromissoServicoModal', 'cancelarCompromissoServico', 'Attempt cancel');

  confirmAction({
    message: t('Tem certeza que deseja cancelar este serviço?'),
    header: t('Confirmação'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        const compromissoServicoData = formRef.value?.getCompromissoServicoData();
        if (!compromissoServicoData?.compromisso_servico_id) return;

        await cancelCompromissoServico(Number(compromissoServicoData.compromisso_servico_id));

        log('CompromissoServicoModal', 'cancelarCompromissoServico', 'Canceled successfully');
        eventBus.emit('globalMessage.success', t('Serviço cancelado com sucesso!'));
        closeEditModal();
        eventBus.emit('compromisso.servico.saved');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const reativarCompromissoServico = (): void => {
  log('CompromissoServicoModal', 'reativarCompromissoServico', 'Attempt reativar');

  confirmAction({
    message: t('Tem certeza que deseja reativar este serviço?'),
    header: t('Confirmação'),
    icon: 'pi pi-question-circle',
    acceptLabel: t('Sim, reativar'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        const compromissoServicoData = formRef.value?.getCompromissoServicoData();
        if (!compromissoServicoData?.compromisso_servico_id) return;

        compromissoServicoData.compromisso_servico_status = 3;

        await saveCompromissoServico(compromissoServicoData as Record<string, unknown>);

        log('CompromissoServicoModal', 'reativarCompromissoServico', 'Reativated successfully');
        eventBus.emit('globalMessage.success', t('Serviço reativado com sucesso!'));
        closeEditModal();
        eventBus.emit('compromisso.servico.saved');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const excluirCompromissoServico = (): void => {
  log('CompromissoServicoModal', 'excluirCompromissoServico', 'Attempt delete');

  confirmAction({
    message: t('Tem certeza que deseja excluir este serviço? Esta ação não pode ser desfeita.'),
    header: t('Confirmação'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim, excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: async () => {
      try {
        const compromissoServicoData = formRef.value?.getCompromissoServicoData();
        if (!compromissoServicoData?.compromisso_servico_id) return;

        await removeCompromissoServico(Number(compromissoServicoData.compromisso_servico_id));

        log('CompromissoServicoModal', 'excluirCompromissoServico', 'Deleted successfully');
        eventBus.emit('globalMessage.success', t('Serviço excluído com sucesso!'));
        closeEditModal();
        eventBus.emit('compromisso.servico.saved');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', message);
      }
    }
  });
};

const resetForm = (): void => {
  log('CompromissoServicoModal', 'resetForm', 'Reset form via formRef');
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleOpenModalEvent = (data?: LegacyOpenPayload): void => {
  log('CompromissoServicoModal', 'handleOpenModalEvent', 'EventBus open event received', data);
  if (!data) return;
  const { compromissoServicoId, compromissoId, orcamentoId } = data;
  abreModal(compromissoServicoId, compromissoId ?? 0, orcamentoId ?? 0);
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('CompromissoServicoModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    await abreModal(data.id, data.compromissoId || 0, data.orcamentoId || 0, data.franquiaId || 0);
  } else if (data?.compromissoId) {
    await abreModalNovo(data.compromissoId, data.orcamentoId || 0, data.franquiaId || 0);
  }
};

const handleModalClose = (): void => {
  closeEditModal();
  closeNewModal();
};

// Lifecycle hooks
onMounted(() => {
  log('CompromissoServicoModal', 'mounted', 'Setting up eventBus listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.compromisso-servico.open', handleModalOpen);
  eventBus.on('modal.compromisso-servico.close', handleModalClose);

  // Legacy events (for backward compatibility)
  eventBus.on('compromisso-servico.open-edit', handleOpenModalEvent);
});

onUnmounted(() => {
  log('CompromissoServicoModal', 'unmounted', 'Cleaning up eventBus listeners');

  // Remove standard modal events
  eventBus.off('modal.compromisso-servico.open', handleModalOpen);
  eventBus.off('modal.compromisso-servico.close', handleModalClose);

  // Remove legacy events
  eventBus.off('compromisso-servico.open-edit', handleOpenModalEvent);
});

// Expose methods for external access
defineExpose({
  abreModal,
  abreModalNovo,
  resetForm
});
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
}
</style>
